<script setup lang="ts">
import {
  computed,
  defineComponent,
  h,
  inject,
  nextTick,
  ref,
  useTemplateRef,
  watch,
  type Component,
  type InputHTMLAttributes,
} from 'vue';
import { i18nInjectionKey } from '../internal';
import { rearmSearchEscapeGuard } from './browserAutofill';
import { currencyDecoration } from './currencyDecoration';
import { type AlignedTextInputProps } from './internal';
import NxTextInput from './NxTextInput.vue';
import { formatCurrencyAmount, roundCurrencyAmount } from './formatCurrencyAmount';

/**
 * ISO 4217 code of the amount (document currency on sales lines, etc.).
 * The edit decoration uses the same locale-aware currency symbol as display
 * mode (for example `$` for USD and `CHF` for CHF in German). This prevents a
 * cell from switching from `245,00 $` to `245,00 USD` when editing starts.
 * Text suffixes use the input's inherited typography, so display/edit swaps
 * also keep the same size and weight.
 *
 * Label components are CACHED per rendered suffix — building a fresh component in a
 * computed each render made Vue re-patch forever and froze the positions grid.
 *
 * `align` goes to NxTextInput with the other props.
 *
 * `allowNull`: an optional amount. Empty (`null` or `''`) shows as an empty
 * field instead of `0,00`, and clearing the field commits `null` — never a
 * `0` nobody typed. Without it an empty value reads as 0, as it always did.
 */
const props = withDefaults(
  defineProps<
    AlignedTextInputProps & {
      currency?: string;
      preventNegativeInput?: boolean;
      /** Empty is a value of its own: shown empty, committed as `null`. */
      allowNull?: boolean;
      onInput?: (payload: InputEvent) => void;
    } & /* @vue-ignore */ Omit<InputHTMLAttributes, 'onInput'>
  >(),
  { currency: 'EUR' }
);

// Track the parent-provided value type so the setter can emit the same type back.
// Reading `valueModel.value` inside set() would go through the getter (always string),
// so we must capture the raw type here on read.
let parentValueType: 'number' | 'string' = 'string';

const [valueModel, valueModelModifiers] = defineModel<
  string | number | null,
  'lazy',
  string,
  string | number
>({
  required: true,
  get(value) {
    parentValueType = typeof value === 'number' ? 'number' : 'string';
    if (props.allowNull && (value == null || value === '')) return '';
    if (value == null) return '0';
    const valueAsString = String(value);
    return valueAsString || '0';
  },
  set(value) {
    if (props.allowNull && (value == null || value === '')) return null;
    if (parentValueType === 'number') {
      return Number(value ?? 0);
    }
    return value || '0';
  },
});

const inputElement = useTemplateRef('nx-currency-input');
let initialValue: string;
let lastValidValue: string;
/**
 * Whether the user changed the field since it got the focus. A field the user
 * only passed through must not commit: its buffer still holds the amount from
 * focus, and the model may have moved on meanwhile (I-3485: Tab from „Rabatt“
 * into „Gesamt“ while the percent write was in flight, the reread brought the
 * new total, and the blur wrote the old total back as a typed amount).
 */
let typedSinceFocus = false;

const i18n = inject(i18nInjectionKey);
const decoration = computed(() =>
  currencyDecoration(props.currency, i18n?.locale.value ?? 'de-DE')
);

/** Stable label constructors keyed by rendered text. */
const currencyLabelCache = new Map<string, Component>();

function currencyLabelChip(label: string): Component {
  let cached = currencyLabelCache.get(label);
  if (!cached) {
    cached = defineComponent({
      name: `CurrencyLabel_${Array.from(label)
        .map((character) => character.codePointAt(0)?.toString(16))
        .join('_')}`,
      setup() {
        return () =>
          h(
            'span',
            {
              class: 'nx-currency-code-chip',
              style: {
                fontSize: '1em',
                fontWeight: 'inherit',
                letterSpacing: 'normal',
                opacity: '1',
                lineHeight: 'inherit',
                userSelect: 'none',
                // Match the display label's spacing. In plain table cells the
                // trailing zone itself is flush with the input's right edge.
                paddingInlineStart: '0.45em',
              },
            },
            label
          );
      },
    });
    currencyLabelCache.set(label, cached);
  }
  return cached;
}

const trailingIcons = computed(() => {
  const extra = props.icons ?? [];
  return [
    ...extra,
    {
      component: currencyLabelChip(decoration.value.label),
      align: 'end' as const,
      occupyPx: decoration.value.reservePx,
    },
  ];
});

/**
 * Characters a keystroke may add to an amount. Anything else (letters, spaces,
 * symbols) is refused before it reaches the field, so it never changes the
 * value or the selection: typing `a` over a selected amount leaves it as it is.
 */
const onBeforeInput = (event: InputEvent) => {
  if (event.inputType !== 'insertText' || !event.data) return;
  const allowed = props.preventNegativeInput ? /[0-9,.]/ : /[0-9,.-]/;
  if (!allowed.test(event.data)) event.preventDefault();
};

/**
 * Normalizes what is in the field after every input and keeps the edit buffer
 * (`shadowNameValue`) equal to exactly what the field shows — the commit on
 * blur reads the buffer, so a character filtered out here can never reach the
 * model (`12a` → `12`, `1a2` → `12`).
 */
const onRawInput = (payload: InputEvent) => {
  typedSinceFocus = true;
  const el = inputElement.value?.inputElement;
  let newValue = el?.value;
  if (newValue) {
    const typed = newValue;
    let newValueNegative = false;
    if (!props.preventNegativeInput && newValue.startsWith('-')) {
      newValue = newValue.slice(1);
      newValueNegative = true;
    }

    // Pasted or dropped text and anything `onBeforeInput` let through: keep
    // digits and decimal marks only.
    newValue = newValue.replaceAll('.', ',').replace(/[^0-9,]/g, '');

    if (newValue === ',') {
      newValue = '0,';
    }

    while (newValue.startsWith('0') && newValue !== '0' && !newValue.startsWith('0,')) {
      newValue = newValue.replace('0', '');
    }

    // A second decimal mark: refuse the input, keep what was there.
    if (!/^[0-9]*(,[0-9]*)?$/.test(newValue)) {
      if (el) el.value = lastValidValue;
      shadowNameValue.value = lastValidValue;
      return;
    }

    if (newValueNegative) newValue = `-${newValue}`;

    if (newValue !== typed && el) {
      const caret = el.selectionStart ?? typed.length;
      el.value = newValue;
      const nextCaret = Math.max(
        0,
        Math.min(newValue.length, caret + newValue.length - typed.length)
      );
      el.setSelectionRange(nextCaret, nextCaret);
    }
  }

  lastValidValue = newValue ?? '';
  shadowNameValue.value = lastValidValue;
  props.onInput?.(payload);

  if (!valueModelModifiers.lazy) {
    valueModel.value = newValue ?? '';
  }
};

const shadowNameValue = ref('');
const isActive = ref(false);

const formatCurrency = (value: string, thousandSteps = true) =>
  formatCurrencyAmount(value, thousandSteps);

/** An empty optional amount stays empty; anything else is formatted. */
const isEmptyAmount = (value: string) => props.allowNull === true && value === '';

const inputModel = computed<string>({
  get() {
    if (!isActive.value) {
      return isEmptyAmount(valueModel.value) ? '' : formatCurrency(valueModel.value);
    }

    return shadowNameValue.value;
  },
  set(newValue) {
    shadowNameValue.value = newValue;
  },
});

const onInputStart = () => {
  const el = inputElement.value?.inputElement;
  const oldLen = el?.value?.length ?? 0;
  const wasSelectedAll =
    !!el && oldLen > 0 && el.selectionStart === 0 && el.selectionEnd === oldLen;

  isActive.value = true;
  typedSinceFocus = false;

  initialValue = valueModel.value;
  shadowNameValue.value = isEmptyAmount(initialValue) ? '' : formatCurrency(initialValue, false);
  lastValidValue = shadowNameValue.value;

  if (wasSelectedAll) {
    nextTick(() => {
      const postEl = inputElement.value?.inputElement;
      postEl?.select();
    });
  }
};

/**
 * Escape discards the typing since focus: the edit buffer shows the amount from
 * focus again, and a plain model — updated on every keystroke — gets that
 * amount back once. The commit on blur then finds the old amount. The key is
 * not prevented (a table cell leaves on it); a listener that prevented it
 * before keeps it. The amount owns this, not NxTextInput (`escapeRestores`
 * off): the base restore would only know the edit buffer, not the model.
 */
const onEscape = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
  if (!isActive.value) return;
  const start = isEmptyAmount(initialValue) ? '' : formatCurrency(initialValue, false);
  const el = inputElement.value?.inputElement;
  if (shadowNameValue.value !== start || (el && el.value !== start)) {
    shadowNameValue.value = start;
    lastValidValue = start;
    if (el) el.value = start;
  }
  if (!valueModelModifiers.lazy && valueModel.value !== initialValue) {
    valueModel.value = initialValue;
  }
  typedSinceFocus = false;
  if (el) rearmSearchEscapeGuard(el);
};

/**
 * The model changed while the field has the focus but nothing was typed (a
 * reread after another field's save): the buffer follows it, so the field shows
 * the current amount and Escape returns to it. A selected amount stays selected
 * — typing still replaces it. Once the user typed, the typing wins.
 */
watch(valueModel, (next) => {
  if (!isActive.value || typedSinceFocus || next === initialValue) return;
  const el = inputElement.value?.inputElement;
  const len = el?.value?.length ?? 0;
  const wasSelectedAll = !!el && len > 0 && el.selectionStart === 0 && el.selectionEnd === len;
  initialValue = next;
  shadowNameValue.value = isEmptyAmount(next) ? '' : formatCurrency(next, false);
  lastValidValue = shadowNameValue.value;
  if (wasSelectedAll) {
    nextTick(() => {
      const postEl = inputElement.value?.inputElement;
      if (postEl && document.activeElement === postEl) postEl.select();
    });
  }
});

/**
 * Commit: more than two fraction digits may be typed; the committed amount is
 * rounded commercially (half away from zero) to two (`1,005` → `1.01`).
 */
const onInputFinish = () => {
  // Passed through without typing: nothing to commit (`typedSinceFocus`).
  if (!typedSinceFocus) {
    isActive.value = false;
    return;
  }
  typedSinceFocus = false;
  const raw = shadowNameValue.value;
  // An optional amount cleared (or left empty): commit null, not 0.
  if (props.allowNull && raw.trim() === '') {
    if (initialValue !== '') valueModel.value = '';
    isActive.value = false;
    return;
  }
  let finalValue = roundCurrencyAmount(raw) ?? raw.replace(',', '.');
  if (finalValue.includes('.')) {
    while (finalValue.endsWith('0')) {
      finalValue = finalValue.slice(0, -1);
    }
    if (finalValue.endsWith('.')) {
      finalValue = finalValue.slice(0, -1);
    }
  }

  if (initialValue === '0' && finalValue === '0') {
    isActive.value = false;
    return;
  }

  valueModel.value = finalValue;
  isActive.value = false;
};
</script>

<template>
  <NxTextInput
    v-bind="{ ...props, onInput: undefined, icons: undefined }"
    ref="nx-currency-input"
    v-model="inputModel"
    :icons="trailingIcons"
    :escape-restores="false"
    @focus="onInputStart"
    @focusout="onInputFinish"
    @beforeinput="onBeforeInput"
    @input="onRawInput"
    @keydown="onEscape"
  />
</template>
